import { useEffect, useMemo, useState } from 'react'
import { Upload, UserSearch, CheckCircle2, Download, IdCard } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { readXlsx } from '../../lib/xlsxRead.js'
import { parseNhsoWorkbook } from '../../lib/nhsoReport.js'
import { useMe } from '../../lib/me.js'
import { canSetClaimStatus } from '../../lib/role.js'
// ⚠️ useEphemeralState, not useState — the hub UNMOUNTS this panel on a tab switch and the
// file is read in the browser and never uploaded, so `result` cannot be re-fetched.
import { useEphemeralState } from '../../lib/persist.js'
import { Card } from '../statistic/statShared.jsx'
import {
  Button, FileDrop, DataTable, useTableState, SubTabs, Banner, Badge, Checkbox, ConfirmDialog,
  PatientName, useToast, fmtDate, fmtMoney,
} from '../../kit'

/* -------------------------------------------------------------------------- */
/* นำเข้า สปสช. — the "รายงานสรุปการขอเบิกชดเชย" .xlsx from the NHSO e-Claim portal,    */
/* diffed against the CRM's own สปสช. claim lines.                                 */
/*                                                                             */
/* THE SAME ENGINE AS ปกส. The match runs through POST /api/sso/reconcile with     */
/* advancepay = 'สปสช.' — ID first, exact name second, then service date + amount. */
/* HCODE on this report is the same MOPH 5-digit code ปกส. calls รหัสสถานพยาบาล, so  */
/* Branch.ssoclinic resolves it to the same branch set (54104 → สนามเป้า/อารีย์/TT). */
/* Only the parser and the "what counts as paid" rule are new:                  */
/*                                                                             */
/*  • PAID = compensated in full (nhsoVerdict 'paid'). Denied, partial (PS late  */
/*    cut, ceiling) and clawback rows are SHOWN, never written — closing those is */
/*    ปิดรายการ on the ledger, one deliberate click by an Admin.                    */
/*  • THE DATE STAMPED IS THE REP DATE. The report carries no transfer date;     */
/*    REP Date is when สปสช. issued the payment report for that batch.            */
/*  • claimRef IS NOT TOUCHED. On a สปสช. line it already holds the EP authen     */
/*    code staff key at the visit — the only per-visit reference there is. The    */
/*    commit sends no claim_ref, and api 570 then leaves claimRef exactly as is.   */
/*  • NO RENAMES. The report prints names without a title, so a rename from it   */
/*    would blank the patient's prefix. Name differences are shown, not offered.   */
/*  • SEVERAL ROWS, ONE LINE (2026-11-04). The file is one row PER ITEM, the CRM  */
/*    one line per visit: a Tooth Taxi school child is ตรวจ 100 + ฟลูออไรด์ 100 here  */
/*    and one ฿200 line there. `combine_rows: true` lets those rows match that line */
/*    as a set. The line is written ONCE, and only when every row that paid it is   */
/*    ชดเชยเต็ม (`group_ok`). From 2026-11-05 the shortcut writes 335 + 350 as two   */
/*    ฿100 lines, and the same flag pairs the file's two ฿100 rows to them by item. */
/* -------------------------------------------------------------------------- */

const ADVANCE_PAY = 'สปสช.'

const VERDICT = {
  paid: { label: 'ชดเชยเต็ม', tone: 'emerald' },
  partial: { label: 'ชดเชยไม่เต็ม', tone: 'amber' },
  denied: { label: 'ไม่ชดเชย', tone: 'rose' },
  clawback: { label: 'เรียกคืน', tone: 'rose' },
}
const SETTLED = new Set(['received', 'partial', 'rejected'])
const CRM_STATUS = { received: 'รับแล้ว', partial: 'ปิด·รับบางส่วน', rejected: 'ปิด·เบิกไม่ได้' }

const money = (n) => fmtMoney(n, { maxDecimals: 2 })
const sumBy = (arr, k) => arr.reduce((t, r) => t + (Number(r[k]) || 0), 0)

const csvCell = (v) => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export default function NhsoImport() {
  const me = useMe()
  const maySetStatus = canSetClaimStatus(me)
  const toast = useToast()

  const [fileName, setFileName] = useEphemeralState('nhso:file', '')
  const [parsing, setParsing] = useState(false)
  const [parsed, setParsed] = useEphemeralState('nhso:parsed', null)
  const [parseError, setParseError] = useState('')

  const [checking, setChecking] = useState(false)
  const [result, setResult] = useEphemeralState('nhso:result', null)
  const [checkError, setCheckError] = useState('')

  const [tab, setTab] = useEphemeralState('nhso:tab', 'write')
  const [fixIds, setFixIds] = useEphemeralState('nhso:fixIds', () => new Set())
  const [confirm, setConfirm] = useState(false)
  const [committing, setCommitting] = useState(false)

  async function onPick(f) {
    setParsed(null); setResult(null); setParseError(''); setCheckError('')
    setFileName(f?.name ?? '')
    if (!f) return
    if (!/\.xlsx$/i.test(f.name)) {
      setParseError(/\.xls$/i.test(f.name)
        ? 'ไฟล์ .xls รุ่นเก่ายังไม่รองรับ — เปิดใน Excel แล้วบันทึกเป็น .xlsx ก่อน'
        : 'ต้องเป็นไฟล์ Excel (.xlsx) ที่ดาวน์โหลดจาก สปสช.')
      return
    }
    setParsing(true)
    try {
      setParsed(parseNhsoWorkbook(await readXlsx(f)))
    } catch (e) {
      setParseError(`อ่านไฟล์ไม่สำเร็จ: ${e?.message || e}`)
    } finally {
      setParsing(false)
    }
  }

  // ล้าง — back to an empty page. Everything derived from the file goes with it, including
  // the ticked repairs and the open bucket, so the next file never inherits a stale choice.
  function reset() {
    setFileName(''); setParsed(null); setResult(null)
    setParseError(''); setCheckError('')
    setFixIds(new Set()); setTab('write'); setConfirm(false)
  }

  async function runCheck() {
    if (!parsed) return
    setChecking(true); setCheckError('')
    try {
      const out = await postJson('/api/sso/reconcile', {
        ssoclinic: parsed.header.hcode,
        start_date: parsed.header.start_date,
        end_date: parsed.header.end_date,
        advancepay: ADVANCE_PAY,
        // Pass 2 in reconcile.js — rows the one-to-one pass left behind may match AS A SET
        // whose amounts sum to one CRM line (ตรวจ 100 + ฟลูออไรด์ 100 → ฿200). สปสช. only.
        combine_rows: true,
        // The REP travels as the "voucher" so reconcile.js hands it back on every row.
        rows: parsed.rows.map((r) => ({
          seq: r.seq, idcard: r.idcard, raw_name: r.raw_name, service_date: r.service_date,
          amount: r.amount, voucher: r.rep_no, transfer_date: r.rep_date, status: r.status,
          // รายการประเภทที่ขอเบิก — pass 1b's tiebreak between two equal lines (ฟลูออไรด์ vs ตรวจ).
          item: r.item,
        })),
      })
      setResult(out)
    } catch (e) {
      const b = e?.body
      setCheckError(b?.error === 'ssoclinic_unknown'
        ? `ไม่พบสาขาที่มีรหัสหน่วยบริการ ${parsed.header.hcode} — ต้องกรอก ssoclinic ของสาขาให้ตรงกับ HCODE ก่อน`
        : b?.detail || b?.error || e?.message || 'ตรวจสอบไม่สำเร็จ')
    } finally {
      setChecking(false)
    }
  }

  /* ── one view row per report row, with its match folded in ──────────────── */
  const view = useMemo(() => {
    if (!parsed || !result) return []
    const m = new Map((result.matched ?? []).map((x) => [x.seq, x]))
    const a = new Map((result.ambiguous ?? []).map((x) => [x.seq, x]))
    const u = new Map((result.unmatched ?? []).map((x) => [x.seq, x]))
    const rows = parsed.rows.map((r) => {
      const hit = m.get(r.seq)
      return {
        ...r,
        key: `r${r.seq}`,
        match: hit ? 'matched' : a.has(r.seq) ? 'ambiguous' : 'unmatched',
        hit: hit ?? null,
        candidates: a.get(r.seq)?.candidates ?? [],
        patient_known: u.get(r.seq)?.patient_known ?? false,
        crm_status: hit ? (hit.advance_status || 'pending') : '',
      }
    })
    // A combined match is one CRM line paid by several report rows, and it is paid only when
    // EVERY one of them is — a denied ฟลูออไรด์ leaves the ฿200 line half-paid, so neither row
    // may write it. `group_ok` is that check; a one-to-one match is always its own group.
    const lineOk = new Map()
    for (const v of rows) if (v.hit?.combined) lineOk.set(v.hit.billing_id, (lineOk.get(v.hit.billing_id) ?? true) && v.verdict === 'paid')
    for (const v of rows) v.group_ok = v.hit?.combined ? lineOk.get(v.hit.billing_id) === true : true
    return rows
  }, [parsed, result])

  const toWrite = useMemo(() => view.filter((v) =>
    v.hit && !SETTLED.has(v.crm_status) && v.verdict === 'paid' && v.group_ok && v.rep_date), [view])
  // A paid row whose combined sibling was not paid is a problem too — it is the row that
  // explains why its line is not being written.
  const problems = useMemo(() => view.filter((v) => v.verdict !== 'paid' || !v.group_ok), [view])
  const notFound = useMemo(() => view.filter((v) => v.match !== 'matched'), [view])
  const done = useMemo(() => view.filter((v) => v.hit && SETTLED.has(v.crm_status)), [view])
  const pending = useMemo(() => (result?.unpaid ?? []).map((x) => ({ ...x, key: `b${x.billing_id}` })), [result])
  const repairs = useMemo(() => view.filter((v) => v.hit?.backfill_idcard || v.hit?.name_mismatch), [view])

  // เติมเลขบัตร defaults ON — it fills a blank or invalid field and destroys nothing.
  useEffect(() => {
    setFixIds(new Set(repairs.filter((v) => v.hit.backfill_idcard).map((v) => v.seq)))
  }, [repairs]) // eslint-disable-line react-hooks/exhaustive-deps
  const idFixes = repairs.filter((v) => v.hit.backfill_idcard && fixIds.has(v.seq))

  // ONE CRM LINE PER WRITE, however many report rows paid it — api 570 must never see the
  // same billing id twice. A combined line takes the LATEST REP date among its rows: it is
  // paid when the last part is.
  const writeLines = useMemo(() => {
    const m = new Map()
    for (const v of toWrite) {
      const id = v.hit.billing_id
      const cur = m.get(id)
      if (!cur) m.set(id, { billing_id: id, rows: [v], rep_no: v.rep_no, rep_date: v.rep_date, amount: v.hit.combined ? v.hit.combined_amount : v.amount })
      else { cur.rows.push(v); if (v.rep_date > cur.rep_date) { cur.rep_date = v.rep_date; cur.rep_no = v.rep_no } }
    }
    return [...m.values()]
  }, [toWrite])

  const repGroups = useMemo(() => {
    const g = new Map()
    for (const l of writeLines) {
      const k = `${l.rep_no}|${l.rep_date}`
      if (!g.has(k)) g.set(k, { rep_no: l.rep_no, rep_date: l.rep_date, lines: [], rows: 0 })
      g.get(k).lines.push(l); g.get(k).rows += l.rows.length
    }
    return [...g.values()].sort((x, y) => x.rep_date.localeCompare(y.rep_date))
  }, [writeLines])

  async function commit() {
    setCommitting(true)
    const failures = []
    let written = 0
    try {
      for (const g of repGroups) {
        try {
          const out = await postJson('/api/billing/status', {
            billing_ids: g.lines.map((l) => l.billing_id),
            status: 'received',
            // No claim_ref on purpose — it would overwrite the EP authen code.
            advance_received_at: g.rep_date,
          })
          written += Number(out?.updated ?? g.lines.length)
        } catch (e) {
          failures.push(`${g.rep_no}: ${e?.body?.detail || e?.message || 'ล้มเหลว'}`)
        }
      }
      let filled = 0
      if (idFixes.length) {
        try {
          const out = await postJson('/api/sso/patient-backfill', {
            idcards: idFixes.map((v) => ({ patient_id: v.hit.patient_id, idcard: v.idcard })),
            names: [],
          })
          filled = Number(out?.filled ?? 0)
          if (out?.rejected?.length) failures.push(`ข้ามเลขบัตร ${out.rejected.length} ราย (${out.rejected[0]?.why ?? ''})`)
        } catch (e) {
          failures.push(`เลขบัตร: ${e?.body?.detail || e?.message || 'ล้มเหลว'}`)
        }
      }
      const msg = [written ? `บันทึกรับเงิน ${written} รายการ` : '', filled ? `เติมเลขบัตร ${filled} ราย` : '']
        .filter(Boolean).join(' · ') || 'ไม่มีอะไรถูกบันทึก'
      if (failures.length) toast.error(`${msg} — ⚠️ ${failures.join(' | ')}`)
      else toast.success(msg)
      setConfirm(false)
      await runCheck()
    } finally {
      setCommitting(false)
    }
  }

  function exportCsv() {
    const crmLabel = (v) => {
      if (!v.hit) return v.match === 'ambiguous' ? 'เลือกเอง' : 'ไม่พบในโปรแกรม'
      const s = CRM_STATUS[v.crm_status] || 'ค้างรับ'
      return v.hit.combined ? `${s} (รวม ${v.hit.combined_seqs.length} แถว = ${v.hit.combined_amount})` : s
    }
    const out = [['REP', 'REP Date', 'Trans ID', 'วันที่รับบริการ', 'เลขบัตร', 'ชื่อในรายงาน', 'HN', 'ชื่อในโปรแกรม', 'สิทธิ', 'รายการ', 'เรียกเก็บ', 'ชดเชย', 'ไม่ชดเชย', 'เรียกคืน', 'ผล สปสช.', 'รหัสปฏิเสธ', 'สถานะในโปรแกรม']]
    for (const v of view) {
      out.push([v.rep_no, v.rep_date, v.trans_id, v.service_date, v.idcard || v.pid, v.raw_name, v.hit?.hn ?? '', v.hit?.crm_name ?? '',
        v.rights, v.item, v.amount, v.compensated, v.denied, v.clawback, VERDICT[v.verdict]?.label ?? v.verdict, v.deny_codes.join(' '), crmLabel(v)])
    }
    for (const p of pending) {
      out.push(['', '', '', p.service_date, p.idcard ?? '', '', p.hn ?? '', p.crm_name ?? '', '', '', p.amount, '', '', '', 'ไม่อยู่ในไฟล์นี้', '', 'ค้างรับ'])
    }
    const blob = new Blob(['﻿' + out.map((r) => r.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `ตามเบิก-สปสช-${result?.ssoclinic ?? ''}-${parsed?.header.start_date ?? ''}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const branchName = (id) => (result?.branches ?? []).find((b) => b.id === id)?.name ?? (id ? `สาขา ${id}` : '—')
  const blocked = !parsed || parsed.errors.length > 0 || !parsed.rows.length
  const t = result?.totals
  const crmDiff = t ? Math.round((t.report_amount - t.crm_amount_in_scope) * 100) / 100 : null

  /* ── columns ──────────────────────────────────────────────────────────── */
  const patientCell = (v) => (v.hit
    ? <PatientName patient={{ patient_id: v.hit.patient_id, fullname: v.hit.crm_name, hn: v.hit.hn }} />
    : <div><div>{v.raw_name || '—'}</div><div className="text-[11.5px] text-slate-400 font-mono">{v.idcard || v.pid || '—'}</div></div>)

  const reportCols = useMemo(() => [
    { key: 'service_date', label: 'วันที่รับบริการ', render: (v) => fmtDate(v.service_date), sortable: true, width: '110px' },
    { key: 'patient', label: 'ผู้ป่วย', render: patientCell, sortValue: (v) => v.hit?.crm_name ?? v.raw_name, sortable: true },
    { key: 'item', label: 'รายการ', render: (v) => <span className="text-[12.5px]">{v.item}</span>, hideBelow: 'md' },
    { key: 'rights', label: 'สิทธิ', hideBelow: 'lg', width: '60px' },
    { key: 'amount', label: 'เรียกเก็บ', align: 'right', render: (v) => money(v.amount), sortable: true, defaultDir: 'desc' },
    { key: 'compensated', label: 'ชดเชย', align: 'right', render: (v) => money(v.compensated) },
    { key: 'verdict', label: 'ผล สปสช.', render: (v) => (
      <div>
        <Badge tone={VERDICT[v.verdict]?.tone}>{VERDICT[v.verdict]?.label ?? v.verdict}</Badge>
        {v.deny_codes.map((c) => (
          <div key={c} className="text-[11.5px] text-rose-700 mt-0.5">{c} · {parsed?.denyCodes?.[c] ?? 'ไม่ทราบความหมาย'}</div>
        ))}
        {v.ps_pct && Number(v.ps_pct) > 0 ? <div className="text-[11.5px] text-amber-700 mt-0.5">ส่งล่าช้า หัก {v.ps_pct}%</div> : null}
        {v.verdict === 'paid' && !v.group_ok ? <div className="text-[11.5px] text-amber-700 mt-0.5">อีกแถวของรายการเดียวกันไม่ได้ชดเชยเต็ม — ยังไม่บันทึก</div> : null}
      </div>
    ) },
    { key: 'crm', label: 'ในโปรแกรม', render: (v) => {
      if (v.match === 'ambiguous') return <span className="text-[12.5px] text-amber-700">เข้าได้ {v.candidates.length} รายการ: {v.candidates.map((c) => c.hn).join(', ')}</span>
      if (!v.hit) return <span className="text-[12.5px] text-slate-500">{v.patient_known ? 'มีคนไข้ แต่ไม่มีรายการวันนั้น' : 'ไม่มีประวัติคนไข้'}</span>
      return (
        <div className="text-[12.5px]">
          {SETTLED.has(v.crm_status) ? <Badge tone="slate">{CRM_STATUS[v.crm_status]}</Badge> : <Badge tone="brand">ค้างรับ</Badge>}
          <div className="text-slate-400 mt-0.5">
            {branchName(v.hit.branch_id)}{v.hit.cross_branch ? ' · ข้ามสาขา' : ''}{v.hit.match_tier === 'fullname' ? ' · จับด้วยชื่อ' : ''}
            {v.hit.combined ? ` · รวม ${v.hit.combined_seqs.length} แถว = ${money(v.hit.combined_amount)}` : ''}
            {v.hit.paired ? (v.hit.pair_kind ? ' · จับคู่ตามรายการ' : ' · จับคู่ตามลำดับ') : ''}
          </div>
        </div>
      )
    } },
  ], [parsed, result]) // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCols = useMemo(() => [
    { key: 'service_date', label: 'วันที่รับบริการ', render: (p) => fmtDate(p.service_date), sortable: true, width: '110px' },
    { key: 'patient', label: 'ผู้ป่วย', render: (p) => <PatientName patient={{ patient_id: p.patient_id, fullname: p.crm_name, hn: p.hn }} />, sortValue: (p) => p.crm_name, sortable: true },
    { key: 'branch', label: 'สาขา', render: (p) => branchName(p.branch_id) },
    { key: 'claim_ref', label: 'เลขอ้างอิง', render: (p) => <span className="font-mono text-[12px]">{p.claim_ref || '—'}</span>, hideBelow: 'md' },
    { key: 'amount', label: 'ยอด', align: 'right', render: (p) => money(p.amount), sortable: true, defaultDir: 'desc' },
  ], [result]) // eslint-disable-line react-hooks/exhaustive-deps

  const lists = { write: toWrite, problem: problems, notfound: notFound, pending, done }
  const active = lists[tab] ? tab : 'write'
  const tableRows = lists[active]
  const tableCols = active === 'pending' ? pendingCols : reportCols
  const table = useTableState(tableRows, tableCols, { pageSize: 50, sort: { key: 'service_date', dir: 'asc' } })
  useEffect(() => { table.setPage(1) }, [active]) // eslint-disable-line react-hooks/exhaustive-deps

  const HINT = {
    write: 'ตรงกับรายการในโปรแกรม สปสช. ชดเชยเต็มจำนวน และยังค้างรับ — กดยืนยันด้านล่างเพื่อบันทึกรับเงิน (ลงวันที่ตาม REP Date) · แถวที่ สปสช. แยกรายการ (เช่น ตรวจ 100 + ฟลูออไรด์ 100) ถูกรวมให้ตรงกับรายการเดียวในโปรแกรม (200) และบันทึกเป็นรายการเดียว',
    problem: 'สปสช. ไม่จ่าย / จ่ายไม่เต็ม / เรียกเงินคืน — ระบบไม่บันทึกให้ ต้องแก้ไขแล้วส่งเบิกใหม่ หรือปิดรายการที่หน้า ตรวจสอบ (Admin)',
    notfound: 'อยู่ในไฟล์ สปสช. แต่หารายการ สปสช. ในโปรแกรมไม่เจอ (เลขบัตร/ชื่อ + วันที่ + ยอดต้องตรงกัน) — อาจคีย์ยอดหรือผู้จ่ายผิด · ระบบลองรวมหลายแถวของคนเดียวกันในวันเดียวกันให้แล้ว ถ้ายังไม่เจอ แปลว่ายอดรวมก็ไม่ตรง',
    pending: 'รายการ สปสช. ในโปรแกรมช่วงวันที่เดียวกัน ที่ไม่อยู่ในไฟล์นี้และยังค้างรับ — ไฟล์หนึ่งคือหนึ่งงวด (REP) จึงอาจอยู่ใน REP อื่น ไม่ได้แปลว่าไม่ได้เงินเสมอไป',
    done: 'ตรงกันและในโปรแกรมบันทึกรับเงิน (หรือปิดรายการ) ไว้แล้ว — ไม่ต้องทำอะไร',
  }

  return (
    <div className="space-y-5">
      <Card icon={<Upload size={17} />} title="นำเข้ารายงานสรุปการขอเบิกชดเชย (สปสช.)"
        subtitle="ดาวน์โหลดไฟล์ Excel จาก e-Claim สปสช. → ลากไฟล์มาวาง → ระบบจะเทียบกับรายการตามเบิก สปสช. ในโปรแกรมให้">
        {!maySetStatus && (
          <Banner tone="warn" className="mb-3">ตรวจและส่งออกได้ตามปกติ · การบันทึกรับเงินต้องให้ GM หรือ Admin เป็นคนกด</Banner>
        )}
        <FileDrop kind="sheet" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          label="ลากไฟล์ Excel (.xlsx) มาวางที่นี่"
          fileName={fileName} busy={parsing || checking || committing} onFile={onPick} onClear={reset} />

        {parseError && <Banner tone="error" className="mt-3">{parseError}</Banner>}

        {parsed && (
          <div className="mt-4 space-y-3">
            <div className="text-[13px] text-slate-600">
              หน่วยบริการ <b>{parsed.header.hcode || '—'}</b> {parsed.header.clinicName}
              {' '}· วันที่รับบริการ {fmtDate(parsed.header.start_date)} – {fmtDate(parsed.header.end_date)}
              {parsed.header.printed_at ? <> · พิมพ์จากระบบ {fmtDate(parsed.header.printed_at)}</> : null}
            </div>

            <DataTable
              rowKey={(r) => r.rep_no}
              rows={parsed.checks.reps ?? []}
              columns={[
                { key: 'rep_no', label: 'REP No.', render: (r) => <span className="font-mono text-[12.5px]">{r.rep_no}</span> },
                { key: 'rep_date', label: 'REP Date', render: (r) => fmtDate(parsed.reps.find((x) => x.rep_no === r.rep_no)?.rep_date) },
                { key: 'rows', label: 'รายการ', align: 'right' },
                { key: 'amount', label: 'เรียกเก็บ', align: 'right', render: (r) => money(r.amount) },
                { key: 'compensated', label: 'ชดเชย', align: 'right', render: (r) => money(r.compensated) },
                { key: 'denied', label: 'ไม่ชดเชย', align: 'right', render: (r) => money(r.denied) },
                { key: 'ok', label: 'ตรงกับ Summary', align: 'center', render: (r) => (r.ok ? <Badge tone="emerald">ตรง</Badge> : <Badge tone="rose">ไม่ตรง</Badge>) },
              ]}
              empty={{ title: 'ไม่พบ REP ในไฟล์' }}
            />

            {parsed.errors.map((e) => <Banner key={e} tone="error">{e}</Banner>)}
            {parsed.warnings.map((w) => <Banner key={w} tone="warn">{w}</Banner>)}
            {!parsed.errors.length && parsed.checks.detail_checked > 0 && !parsed.checks.detail_mismatch && (
              <Banner tone="success">อ่านไฟล์ครบ — ยอดตรงกับแท็บ Summary ทุก REP และตรงกับแท็บ Detail ทั้ง {parsed.checks.detail_checked} ราย</Banner>
            )}

            <div className="flex items-center gap-3">
              <Button icon={UserSearch} loading={checking} disabled={blocked}
                disabledReason={parsed.errors.length ? 'ไฟล์ไม่ผ่านการตรวจยอด — ห้ามใช้ผลนี้บันทึก' : undefined}
                onClick={runCheck}>ตรวจสอบกับระบบ</Button>
              <span className="text-[12.5px] text-slate-500">ขั้นตอนนี้ยังไม่บันทึกอะไรทั้งสิ้น</span>
            </div>
            {checkError && <Banner tone="error">{checkError}</Banner>}
          </div>
        )}
      </Card>

      {result && parsed && (
        <>
          <Card icon={<CheckCircle2 size={17} />} title="ผลการเทียบ"
            subtitle={`สาขาที่ใช้รหัส ${result.ssoclinic}: ${(result.branches ?? []).map((b) => b.name).join(' · ') || '—'}`}
            right={<Button variant="secondary" size="sm" icon={Download} onClick={exportCsv}>CSV</Button>}>
            <Banner tone={crmDiff === 0 ? 'success' : 'warn'} className="mb-3"
              title={crmDiff === 0 ? 'ยอดตรงกับระบบ' : 'ยอดไม่ตรงกับระบบ'}>
              ไฟล์ สปสช. เรียกเก็บ {money(t.report_amount)} ฿ ({t.report_rows} รายการ) · ในโปรแกรมช่วงวันที่เดียวกัน
              {' '}{money(t.crm_amount_in_scope)} ฿ ({t.crm_lines_in_scope} รายการ)
              {crmDiff !== 0 ? ` · ต่าง ${money(Math.abs(crmDiff))} ฿ — ${crmDiff > 0 ? 'ไฟล์มากกว่า ดู "ไม่พบในโปรแกรม"' : 'โปรแกรมมากกว่า ดู "ค้างรับ ไม่อยู่ในไฟล์"'}` : ''}
            </Banner>

            <SubTabs className="mb-2" value={active} onChange={setTab} items={[
              { key: 'write', label: 'รอบันทึกรับเงิน', badge: toWrite.length, badgeTone: toWrite.length ? 'alert' : 'count' },
              { key: 'problem', label: 'สปสช. ไม่จ่าย / ไม่เต็ม', badge: problems.length, badgeTone: problems.length ? 'alert' : 'count' },
              { key: 'notfound', label: 'ไม่พบในโปรแกรม', badge: notFound.length, badgeTone: notFound.length ? 'alert' : 'count' },
              { key: 'pending', label: 'ค้างรับ ไม่อยู่ในไฟล์', badge: pending.length },
              { key: 'done', label: 'รับแล้ว', badge: done.length },
            ]} />
            <div className="text-[12.5px] text-slate-500 mb-2">{HINT[active]}</div>
            <DataTable columns={tableCols} rowKey={(r) => r.key} {...table.tableProps}
              empty={{ title: 'ไม่มีรายการในกลุ่มนี้' }} />
          </Card>

          {repairs.length > 0 && (
            <Card icon={<IdCard size={17} />} title="ข้อมูลคนไข้ไม่ตรงกับ สปสช."
              subtitle="เติมเลขบัตรที่ว่าง/ผิดรูปแบบได้พร้อมการบันทึก · ชื่อที่ไม่ตรงแสดงให้ดูเท่านั้น (ไฟล์ สปสช. ไม่มีคำนำหน้า จึงไม่แก้ชื่อให้)">
              <DataTable rowKey={(v) => v.key} rows={repairs} columns={[
                { key: 'patient', label: 'ในโปรแกรม', render: (v) => <PatientName patient={{ patient_id: v.hit.patient_id, fullname: v.hit.crm_name, hn: v.hit.hn }} /> },
                { key: 'raw_name', label: 'ชื่อใน สปสช.', render: (v) => (v.hit.name_mismatch
                  ? <span className="text-amber-800">{v.raw_name}</span> : <span className="text-slate-400">ตรงกัน</span>) },
                { key: 'idcard', label: 'เลขบัตร', render: (v) => (v.hit.backfill_idcard
                  ? <span className="font-mono text-[12px]">{v.hit.crm_idcard || 'ว่าง'} → {v.idcard}</span>
                  : <span className="text-slate-400">ตรงกัน</span>) },
                { key: 'fix', label: 'เติมเลขบัตร', align: 'center', render: (v) => (v.hit.backfill_idcard
                  ? <Checkbox aria-label="เติมเลขบัตร" checked={fixIds.has(v.seq)} onChange={() => setFixIds((prev) => {
                    const n = new Set(prev); if (n.has(v.seq)) n.delete(v.seq); else n.add(v.seq); return n
                  })} />
                  : '—') },
              ]} />
              {t.idcard_conflict ? (
                <Banner tone="error" className="mt-3">
                  มี {t.idcard_conflict} รายที่ในโปรแกรมมีเลขบัตร 13 หลักอยู่แล้วแต่ไม่ตรงกับ สปสช. — ระบบไม่แก้ให้ ต้องตรวจด้วยตาก่อน
                </Banner>
              ) : null}
            </Card>
          )}

          <Card icon={<CheckCircle2 size={17} />} title="บันทึกผล"
            subtitle="บันทึกรับเงินแยกตาม REP ลงวันที่ตาม REP Date · ไม่แก้เลขอ้างอิง EP ในรายการ"
            right={<Button icon={CheckCircle2} disabled={!maySetStatus || (!toWrite.length && !idFixes.length)}
              disabledReason={!maySetStatus ? 'บันทึกได้เฉพาะ GM หรือ Admin' : 'ไม่มีรายการที่ต้องบันทึก'}
              onClick={() => setConfirm(true)}>ยืนยันและบันทึก</Button>}>
            {!toWrite.length && !idFixes.length ? (
              <div className="text-[13px] text-slate-600">ไม่มีรายการที่ต้องบันทึกเพิ่ม</div>
            ) : (
              <DataTable rowKey={(g) => g.rep_no} rows={repGroups} columns={[
                { key: 'rep_no', label: 'REP', render: (g) => <span className="font-mono text-[12.5px]">{g.rep_no}</span> },
                { key: 'rep_date', label: 'ลงวันที่รับเงิน', render: (g) => fmtDate(g.rep_date) },
                { key: 'n', label: 'รายการ', align: 'right', render: (g) => g.lines.length },
                { key: 'rows', label: 'แถวในไฟล์', align: 'right', render: (g) => g.rows },
                { key: 'amt', label: 'ยอด', align: 'right', render: (g) => money(sumBy(g.lines, 'amount')) },
              ]} empty={{ title: 'ไม่มีรายการรับเงิน', hint: idFixes.length ? `เติมเลขบัตร ${idFixes.length} ราย` : undefined }} />
            )}
          </Card>
        </>
      )}

      {confirm && (
        <ConfirmDialog
          title="ยืนยันและบันทึก?"
          message={[
            toWrite.length ? `บันทึกรับเงิน ${writeLines.length} รายการ (${toWrite.length} แถวในไฟล์) ${money(sumBy(writeLines, 'amount'))} ฿ จาก ${repGroups.length} REP` : '',
            idFixes.length ? `เติมเลขบัตร ${idFixes.length} ราย` : '',
          ].filter(Boolean).join(' · ')}
          confirmLabel="บันทึก"
          busy={committing}
          onConfirm={commit}
          onCancel={() => setConfirm(false)}
        />
      )}
    </div>
  )
}
